import { z } from 'zod';
import { IdSchema, DateTimeSchema, PaginationParamsSchema, HttpOrInternalFileUrlSchema, } from '../../common';
export const SubmissionStatusSchema = z.enum(['pending', 'submitted', 'graded']);
// Düzeltme 5 · D5-5 — ödev türü:
//  writing      → öğrenci kendi kitabından çözüp yükler (varsayılan)
//  online_exam  → oluşturulmuş bir sınav atanır (examId)
//  worksheet    → oluşturulmuş bir yaprak test atanır (worksheetId)
export const HomeworkKindSchema = z.enum(['writing', 'online_exam', 'worksheet']);
// ============================================
// Homework
// ============================================
export const HomeworkSchema = z.object({
    id: IdSchema,
    courseId: IdSchema.nullable(),
    title: z.string(),
    description: z.string().nullable(),
    subjectId: IdSchema.nullable(),
    topicId: IdSchema.nullable(),
    dueAt: DateTimeSchema.nullable(),
    maxPoints: z.number().int(),
    allowLateSubmission: z.boolean(),
    attachmentUrl: z.string().nullable(),
    // Düzeltme 5 · D5-5 — ödev türü + atanan sınav/yaprak test referansları
    kind: HomeworkKindSchema,
    examId: IdSchema.nullable(),
    worksheetId: IdSchema.nullable(),
    createdById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    submissionCount: z.number().int().optional(),
    courseName: z.string().nullable().optional(),
    subjectName: z.string().nullable().optional(),
    gradeLevel: z.number().int().nullable().optional(),
    teacherName: z.string().nullable().optional(),
    createdByName: z.string().nullable().optional(),
});
export const CreateHomeworkSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(8000).optional(),
    courseId: IdSchema.optional(),
    subjectId: IdSchema.optional(),
    topicId: IdSchema.optional(),
    dueAt: z.string().datetime().optional(),
    maxPoints: z.coerce.number().int().min(1).max(1000).optional(),
    allowLateSubmission: z.boolean().optional(),
    attachmentUrl: HttpOrInternalFileUrlSchema.optional(),
    // Düzeltme 5 · D5-5 — tür + atanan sınav/yaprak test. online_exam ise examId,
    // worksheet ise worksheetId beklenir (API service'te doğrulanır).
    kind: HomeworkKindSchema.optional(),
    examId: IdSchema.optional(),
    worksheetId: IdSchema.optional(),
});
// Güncellemede null, mevcut eğitim bağını açıkça kaldırır. Create tarafında
// courseId hâlâ yalnız geçerli bir UUID olabilir.
export const UpdateHomeworkSchema = CreateHomeworkSchema.partial().extend({
    courseId: IdSchema.nullable().optional(),
    attachmentUrl: HttpOrInternalFileUrlSchema.nullable().optional(),
});
export const HomeworkQuerySchema = PaginationParamsSchema.extend({
    search: z.string().optional(),
    courseId: IdSchema.optional(),
    subjectId: IdSchema.optional(),
    dueBefore: z.string().datetime().optional(),
});
// ============================================
// Submission
// ============================================
// Ek tipi: foto / video / dokuman.
export const HomeworkAttachmentKindSchema = z.enum(['image', 'video', 'file']);
// PDF / data: URL — http(s), data: veya file-storage servisinin korumalı
// `/api/v1/files/:uuid/serve` yolu kabul edilir. Herhangi bir göreli yolun
// kabul edilmesi yerine yalnız bu tanımlı iç kaynak biçimine izin verilir.
const UrlOrDataUrl = z
    .string()
    .min(1)
    .max(20_000_000)
    .refine((v) => v.startsWith('data:') || HttpOrInternalFileUrlSchema.safeParse(v).success, 'url must be a https URL, data: URL or an internal file serve path');
export const HomeworkSubmissionAttachmentSchema = z.object({
    id: IdSchema,
    submissionId: IdSchema,
    url: z.string(),
    kind: HomeworkAttachmentKindSchema,
    mimeType: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    createdAt: DateTimeSchema,
});
export const HomeworkSubmissionAttachmentInputSchema = z.object({
    url: UrlOrDataUrl,
    kind: HomeworkAttachmentKindSchema,
    mimeType: z.string().max(255).optional(),
    sizeBytes: z.coerce
        .number()
        .int()
        .nonnegative()
        .max(10 * 1024 * 1024)
        .optional(),
});
export const HomeworkSubmissionSchema = z.object({
    id: IdSchema,
    homeworkId: IdSchema,
    studentUserId: IdSchema,
    status: SubmissionStatusSchema,
    content: z.string().nullable(),
    attachmentUrl: z.string().nullable(),
    submittedAt: DateTimeSchema.nullable(),
    gradedAt: DateTimeSchema.nullable(),
    earnedPoints: z.number().int().nullable(),
    feedback: z.string().nullable(),
    gradedById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    attachments: z.array(HomeworkSubmissionAttachmentSchema).optional(),
    studentName: z.string().nullable().optional(),
    homeworkTitle: z.string().nullable().optional(),
});
export const SubmitHomeworkSchema = z.object({
    content: z.string().max(40_000).optional(),
    attachmentUrl: HttpOrInternalFileUrlSchema.optional(),
    // Yeni: coklu dosya — foto/video/pdf karisik, max 10
    attachments: z.array(HomeworkSubmissionAttachmentInputSchema).max(10).optional(),
});
export const GradeSubmissionSchema = z.object({
    earnedPoints: z.coerce.number().int().min(0),
    feedback: z.string().max(4000).optional(),
});
// ============================================
// Homework Assignment — course / camp / student
// ============================================
export const HomeworkAssignmentTargetSchema = z.enum(['course', 'camp', 'student']);
export const HomeworkAssignmentSchema = z.object({
    id: IdSchema,
    homeworkId: IdSchema,
    target: HomeworkAssignmentTargetSchema,
    targetId: IdSchema,
    assignedById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    targetName: z.string().nullable().optional(),
});
export const CreateHomeworkAssignmentSchema = z.object({
    target: HomeworkAssignmentTargetSchema,
    targetIds: z.array(IdSchema).min(1).max(200),
});
//# sourceMappingURL=homework.js.map